import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBag, Heart, User, Search, Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import TopBar from './TopBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ cartCount = 0, wishlistCount = 0 }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileGiftsOpen, setMobileGiftsOpen] = useState(false);
  const [mobileCollectionsOpen, setMobileCollectionsOpen] = useState(false);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/collections`)
      .then(({ data }) => setCollections(data))
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7] border-b border-[rgba(45,59,38,0.1)]">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6">
        {/* Top Row: Logo left, Icons right */}
        <div className="flex items-center justify-between py-0.5">
          {/* Left: Mobile Menu + Logo */}
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="md:hidden text-[#2D3B26]"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to="/" data-testid="logo-link" className="flex items-center justify-start">
              <div className="h-10 sm:h-11 md:h-12 w-28 sm:w-32 md:w-36 flex items-center overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_gen-z-jewels-1/artifacts/8dp6l3ul_nova.png" alt="NOVA" className="h-full w-auto object-contain" style={{ transform: 'scale(2.2)', transformOrigin: 'left center' }} />
              </div>
            </Link>
          </div>

          {/* Right Icons */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              data-testid="search-toggle-button"
              className="text-[#2D3B26] hover:text-[#C27054] transition-colors duration-300"
            >
              <Search size={18} />
            </button>

            {user ? (
              <>
                <Link
                  to="/wishlist"
                  data-testid="wishlist-link"
                  className="relative text-[#2D3B26] hover:text-[#C27054] transition-colors duration-300"
                >
                  <Heart size={20} />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#C27054] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {wishlistCount}
                    </span>
                  )}
                </Link>

                <Link
                  to="/cart"
                  data-testid="cart-link"
                  className="relative text-[#2D3B26] hover:text-[#C27054] transition-colors duration-300"
                >
                  <ShoppingBag size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#C27054] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>

                <div className="relative">
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)} data-testid="user-menu-button" className="text-[#2D3B26] hover:text-[#C27054] transition-colors duration-300">
                    <User size={20} />
                  </button>
                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                      <div className="absolute right-0 mt-2 w-48 bg-[#FDFBF7] border border-[rgba(45,59,38,0.1)] rounded-2xl shadow-lg z-50">
                        <div className="py-2">
                          {user.role === 'admin' && (
                            <Link
                              to="/admin"
                              data-testid="admin-panel-link"
                              className="block px-4 py-2 text-sm text-[#C27054] font-semibold hover:bg-[#E4E8DF] transition-colors"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              Admin Panel
                            </Link>
                          )}
                          <Link
                            to="/orders"
                            data-testid="orders-link"
                            className="block px-4 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] transition-colors"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            My Orders
                          </Link>
                          <button
                            onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                            data-testid="logout-button"
                            className="block w-full text-left px-4 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] transition-colors"
                          >
                            Logout
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="wishlist-link"
                  className="relative text-[#2D3B26] hover:text-[#C27054] transition-colors duration-300"
                >
                  <Heart size={20} />
                </Link>

                <Link
                  to="/login"
                  data-testid="cart-link"
                  className="relative text-[#2D3B26] hover:text-[#C27054] transition-colors duration-300"
                >
                  <ShoppingBag size={20} />
                </Link>

                <Link
                  to="/login"
                  data-testid="login-link"
                  className="text-[#2D3B26] hover:text-[#C27054] transition-colors duration-300"
                >
                  <User size={20} />
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Desktop Navigation - Centered below logo */}
        {/* Nav Row: Tagline (left) + Centered Navigation */}
        <div className="hidden md:flex items-center pb-1 relative">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#7A8A73] font-semibold">New Gold Era</span>
          <nav className="absolute left-1/2 -translate-x-1/2 flex items-center space-x-10">

          {/* Gold Dropdown */}
          <div className="relative group">
            <Link
              to="/products?category=gold"
              data-testid="nav-gold"
              className="text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054] transition-colors duration-300 py-2"
            >
              Gold
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-[#FDFBF7] border border-[rgba(45,59,38,0.1)] rounded-2xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <div className="py-3">
                <Link to="/products?category=gold&type=ring" data-testid="nav-gold-ring" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Ring</Link>
                <Link to="/products?category=gold&type=pendant" data-testid="nav-gold-pendant" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Pendant</Link>
                <Link to="/products?category=gold&type=earring" data-testid="nav-gold-earring" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Earring</Link>
                <Link to="/products?category=gold&type=necklace" data-testid="nav-gold-necklace" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Necklace</Link>
                <Link to="/products?category=gold&type=nosepin" data-testid="nav-gold-nosepin" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Nosepin</Link>
                <Link to="/products?category=gold&type=bracelet" data-testid="nav-gold-bracelet" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Bracelet</Link>
                <Link to="/products?category=gold&type=mangalsutra" data-testid="nav-gold-mangalsutra" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Mangalsutra</Link>
                <div className="border-t border-[rgba(45,59,38,0.1)] mt-2 pt-2">
                  <Link to="/products?category=gold&type=all" className="block px-5 py-2 text-sm font-semibold text-[#C27054] hover:bg-[#E4E8DF] transition-colors">View All Gold</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Silver Dropdown */}
          <div className="relative group">
            <Link
              to="/products?category=silver"
              data-testid="nav-silver"
              className="text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054] transition-colors duration-300 py-2"
            >
              Silver
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-[#FDFBF7] border border-[rgba(45,59,38,0.1)] rounded-2xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <div className="py-3">
                <Link to="/products?category=silver&type=ring" data-testid="nav-silver-ring" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Ring</Link>
                <Link to="/products?category=silver&type=pendant" data-testid="nav-silver-pendant" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Pendant</Link>
                <Link to="/products?category=silver&type=earring" data-testid="nav-silver-earring" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Earring</Link>
                <Link to="/products?category=silver&type=necklace" data-testid="nav-silver-necklace" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Necklace</Link>
                <Link to="/products?category=silver&type=bracelet" data-testid="nav-silver-bracelet" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Bracelet</Link>
                <Link to="/products?category=silver&type=chain" data-testid="nav-silver-chain" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Chain</Link>
                <div className="border-t border-[rgba(45,59,38,0.1)] mt-2 pt-2">
                  <Link to="/products?category=silver&type=all" className="block px-5 py-2 text-sm font-semibold text-[#C27054] hover:bg-[#E4E8DF] transition-colors">View All Silver</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Gift Dropdown */}
          <div className="relative group">
            <Link
              to="/products?gift=true"
              data-testid="nav-gifts"
              className="text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054] transition-colors duration-300 py-2"
            >
              Gifts
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-72 bg-[#FDFBF7] border border-[rgba(45,59,38,0.1)] rounded-2xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <div className="py-3">
                <div className="px-5 py-2">
                  <p className="text-xs uppercase tracking-widest font-semibold text-[#2D3B26] mb-2">Gift For</p>
                  <Link to="/products?gift=her" data-testid="nav-gift-her" className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054] transition-colors">Gift for Her</Link>
                  <Link to="/products?gift=him" data-testid="nav-gift-him" className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054] transition-colors">Gift for Him</Link>
                </div>
                <div className="border-t border-[rgba(45,59,38,0.1)] mt-2 pt-2 px-5 py-2">
                  <p className="text-xs uppercase tracking-widest font-semibold text-[#2D3B26] mb-2">By Price</p>
                  <Link to="/products?max_price=5000" data-testid="nav-gift-under-5k" className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054] transition-colors">Under &#8377;5,000</Link>
                  <Link to="/products?max_price=10000" data-testid="nav-gift-under-10k" className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054] transition-colors">Under &#8377;10,000</Link>
                  <Link to="/products?max_price=15000" data-testid="nav-gift-under-15k" className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054] transition-colors">Under &#8377;15,000</Link>
                  <Link to="/products?max_price=25000" data-testid="nav-gift-under-25k" className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054] transition-colors">Under &#8377;25,000</Link>
                  <Link to="/products?max_price=30000" data-testid="nav-gift-under-30k" className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054] transition-colors">Under &#8377;30,000</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Men Dropdown */}
          <div className="relative group">
            <Link
              to="/products?gender=men"
              data-testid="nav-men"
              className="text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054] transition-colors duration-300 py-2"
            >
              Men
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-[#FDFBF7] border border-[rgba(45,59,38,0.1)] rounded-2xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <div className="py-3">
                <Link to="/products?gender=men&type=ring" data-testid="nav-men-ring" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Ring</Link>
                <Link to="/products?gender=men&type=chain" data-testid="nav-men-chain" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Chains</Link>
                <Link to="/products?gender=men&type=pendant" data-testid="nav-men-pendant" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Pendant</Link>
                <Link to="/products?gender=men&type=earring" data-testid="nav-men-earring" className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors">Earring</Link>
                <div className="border-t border-[rgba(45,59,38,0.1)] mt-2 pt-2">
                  <Link to="/products?gender=men" className="block px-5 py-2 text-sm font-semibold text-[#C27054] hover:bg-[#E4E8DF] transition-colors">View All Men</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Collection Dropdown */}
          <div className="relative group">
            <Link
              to="/products"
              data-testid="nav-collection"
              className="text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054] transition-colors duration-300 py-2"
            >
              Collection
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 bg-[#FDFBF7] border border-[rgba(45,59,38,0.1)] rounded-2xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <div className="py-3 max-h-[70vh] overflow-y-auto">
                {collections.length === 0 ? (
                  <p className="px-5 py-2 text-xs text-[#7A8A73] italic">No collections yet</p>
                ) : (
                  collections.map((c) => (
                    <Link
                      key={c.id}
                      to={`/products?collection=${c.slug}`}
                      data-testid={`nav-collection-${c.slug}`}
                      className="block px-5 py-2 text-sm text-[#475740] hover:bg-[#E4E8DF] hover:text-[#C27054] transition-colors"
                    >
                      {c.name}
                    </Link>
                  ))
                )}
                <div className="border-t border-[rgba(45,59,38,0.1)] mt-2 pt-2">
                  <Link to="/products" className="block px-5 py-2 text-sm font-semibold text-[#C27054] hover:bg-[#E4E8DF] transition-colors">View All Collections</Link>
                </div>
              </div>
            </div>
          </div>

          {/* About */}
          <Link
            to="/about"
            data-testid="nav-about"
            className="text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054] transition-colors duration-300"
          >
            About
          </Link>
        </nav>
        </div>

        {/* Search Bar */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4"
            >
              <form onSubmit={handleSearch} data-testid="search-form">
                <input
                  type="text"
                  placeholder="Search jewelry..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="w-full px-6 py-3 bg-[#E4E8DF] border-none rounded-full text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                />
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mt-4 space-y-2"
              data-testid="mobile-menu"
            >
              <Link
                to="/products"
                data-testid="mobile-nav-all-jewelry"
                className="block py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Jewelry
              </Link>
              <Link
                to="/products?category=gold"
                data-testid="mobile-nav-gold"
                className="block py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Gold
              </Link>
              <Link
                to="/products?category=silver"
                data-testid="mobile-nav-silver"
                className="block py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Silver
              </Link>

              {/* Gifts expandable */}
              <div>
                <button
                  onClick={() => setMobileGiftsOpen(!mobileGiftsOpen)}
                  data-testid="mobile-nav-gifts-toggle"
                  className="w-full flex items-center justify-between py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                >
                  <span>Gifts</span>
                  <ChevronDown size={14} className={`transition-transform duration-300 ${mobileGiftsOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {mobileGiftsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className="pl-4 pb-2"
                    >
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-[#2D3B26] mt-2 mb-1">Gift For</p>
                      <Link to="/products?gift=her" data-testid="mobile-nav-gift-her" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]">Gift for Her</Link>
                      <Link to="/products?gift=him" data-testid="mobile-nav-gift-him" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]">Gift for Him</Link>
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-[#2D3B26] mt-3 mb-1">By Price</p>
                      <Link to="/products?max_price=5000" data-testid="mobile-nav-gift-under-5k" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]">Under &#8377;5,000</Link>
                      <Link to="/products?max_price=10000" data-testid="mobile-nav-gift-under-10k" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]">Under &#8377;10,000</Link>
                      <Link to="/products?max_price=15000" data-testid="mobile-nav-gift-under-15k" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]">Under &#8377;15,000</Link>
                      <Link to="/products?max_price=25000" data-testid="mobile-nav-gift-under-25k" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]">Under &#8377;25,000</Link>
                      <Link to="/products?max_price=30000" data-testid="mobile-nav-gift-under-30k" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]">Under &#8377;30,000</Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/products?gender=men"
                data-testid="mobile-nav-men"
                className="block py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Men
              </Link>
              {/* Collection expandable */}
              <div>
                <button
                  onClick={() => setMobileCollectionsOpen(!mobileCollectionsOpen)}
                  data-testid="mobile-nav-collection-toggle"
                  className="w-full flex items-center justify-between py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                >
                  <span>Collection</span>
                  <ChevronDown size={14} className={`transition-transform duration-300 ${mobileCollectionsOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {mobileCollectionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className="pl-4 pb-2"
                    >
                      {collections.map((c) => (
                        <Link
                          key={c.id}
                          to={`/products?collection=${c.slug}`}
                          data-testid={`mobile-nav-collection-${c.slug}`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block py-1.5 text-sm text-[#475740] hover:text-[#C27054]"
                        >
                          {c.name}
                        </Link>
                      ))}
                      <Link
                        to="/products"
                        data-testid="mobile-nav-collection-all"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-1.5 text-sm font-semibold text-[#C27054] mt-1"
                      >
                        View All Collections →
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <Link
                to="/about"
                data-testid="mobile-nav-about"
                className="block py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/virtual-tryon"
                data-testid="mobile-nav-virtual-tryon"
                className="block py-2 text-sm font-sans uppercase tracking-widest text-[#475740] hover:text-[#C27054]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Virtual Try-On
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;